import { Controller } from '../Controller';
import { Helper } from './Helper';
import { AppConfig } from '../../AppConfig';
import { AppHelper } from '../../helpers/AppHelper';
import { AuthError, ApiDataError, ApiError } from '../errors';

export class ApiHelper {

  static State = Object.freeze({
    LOADING: 1,
    READY: 2,
    ERROR: 3
  });

  static LOADING_MODE = Object.freeze({
    PAGE_LOAD: 1,
    VIEW_LOAD: 2,
    INSTANT_LOAD: 3,
    TABLE_LOAD: 4
  });

  static ORDER_UI_MODE = Object.freeze({
    STATIC: 1,
    INTERACTIVE: 2,
  });

  static callWithProgressView(caller, options, callback) {
    caller.refs.apiProgressView.progress();
    ApiHelper.call(options, function (err, data) {
      if (err) {
        caller.refs.apiProgressView.error(err);
        return callback(err)
      } else {
        const successMsg = options.successMsg || Helper.getString('successful')
        caller.refs.apiProgressView.success(successMsg);
        return callback(err, data)
      }
    })
  }

  static async callSSAwait(options) {
    try {
      const response = await ApiHelper.callAwait(options)
      return { props: {
        datass: response,
        pageMetaData: Helper.selectDict('title,descr,metaTitle,metaDescr,metaKeywords', response)
      }}
    } catch(err) {
      return { props: { err: { errMsg: Helper.getErrorMsg(err) } } }
    }
  }

  static async callAwait(options) {
    return await Helper.promisify(ApiHelper.call)(options)
  }

  static call(inputOptions, callback) {
    const options = Object.assign({}, inputOptions)
    if (!options.endPoint) {
      if(options.ms) {
        options.endPoint = ApiHelper.makeUrlPath2(
          {ms: options.ms, paths: options.paths, queryParams: options.queryParams})
      } else {
        options.endPoint = ApiHelper.makeUrlPath(options.paths, options.queryParams)
      }
    }
    // console.log('call:', options.endPoint)
    if (AppConfig.ALLOWMOCK && Controller.get().mockHelper().hasMock(options)) {
      return Controller.get().mockHelper().call(options, callback)
    }
    ApiHelper._fetch(options).then((response) => {
      response.json().then((responseJson) => {
        return callback(null, responseJson)
      }).catch((parseError) => {
        return callback(new ApiDataError())
      });
    }).catch((error) => {
      //console.log('error:', error)
      // if (error.status === 401) {
      //   return callback(new AuthError())
      // }
      if (!error.json) {
        return callback(new ApiError())
      }
      error.json().then((errorJson) => {
        if (error.status === 401) {
          if (errorJson.respCode === 'tokenError') {
            Controller.get().logout()
            return
          }else{
            return callback(new AuthError())
          }
        }
        return callback(errorJson)
      }).catch((parseError) => {
        return callback(new ApiDataError())
      });
    });
  }

  static makeUrlPath(paths, queryParams) {
    let url = AppConfig.ENDPOINT;
    if (AppConfig.ENDPOINT_AUTH_MICROSERVICE && Helper.inArray(['orgs', 'users'], paths[0])) {
      url = AppConfig.ENDPOINT_AUTH_MICROSERVICE
    }
    for (const path of paths) {
      url = url + '/' + path;
    }
    if (queryParams) {
      const qs = Helper.encodeQueryString(queryParams);
      if (qs) {
        url += '?' + qs;
      }
    }
    return url;
  }

  static makeUrlPath2(options) {
    const {ms, paths, queryParams} = options
    let url = ms || AppConfig.ENDPOINT
    if (AppConfig.ENDPOINT_AUTH_MICROSERVICE && Helper.inArray(['orgs', 'users'], paths[0])) {
      url = AppConfig.ENDPOINT_AUTH_MICROSERVICE
    }
    for (const path of paths) {
      url = url + '/' + path;
    }
    if (queryParams) {
      const qs = Helper.encodeQueryString(queryParams);
      if (qs) {
        url += '?' + qs;
      }
    }
    return url;
  }

  static _fetch(apiOptions) {
    const options = Object.assign({}, apiOptions)
    //console.log('_fetch:', options.method, options.endPoint)
    return new Promise((resolve, reject) => {
      ApiHelper._preAuth(options, function (err) {
        if (err) {
          //console.log('Auth Failed');
          reject(err);
        } else {
          const input = {
            method: options.method,
            headers: {}
          };
          if (options.jsonBody) {
            input.body = JSON.stringify(options.jsonBody);
            input.headers['Content-Type'] = 'application/json';
          }
          if (options.headers) {
            Object.assign(input.headers, options.headers);
          }
          if (options.authWithKey) {
            input.headers.Authorization = 'Key ' + AppConfig.APPAK;
          } else {
            input.headers.Authorization = 'Bearer ' + Controller.get().userMgr().getUserToken();
          }
          //console.log('fetch:', options.method, options.endPoint, input)
          if (!options.endPoint) {
            options.endPoint = ApiHelper.makeUrlPath(options.paths, options.queryParams)
          }
          fetch(options.endPoint, input).then((response) => {
            //console.log('_fetch response:', response);
            if (response.ok) {
              resolve(response);
            } else {
              console.error('_fetch not ok:', response);
              reject(response);
            }
          }).catch((error) => {
            console.error('_fetch error:', error);
            reject(error);
          });
        }
      });
    });
  }

  static _preAuth(options, callback) {
    //console.log('_preAuth');

    if (options.authWithKey) {
      callback(null);
      return;
    }

    const cognitoUser = Controller.get().loginMgr().getCurrentUser()
    if (!cognitoUser) {
      callback(Helper.getString('loginErr'));
      return
    }
    Controller.get().loginMgr().fetchUserToken(cognitoUser, function (err) {
      if (err) {
        console.error(err)
        callback(err);
        return;
      }
      // Token should by now be cached in
      callback(null);
    })
  }

  static async trackContactStaffActivity(current, subActivityType) {
    try {
      if(current && subActivityType) {
        await ApiHelper.callAwait({
          method: 'POST',
          endPoint: ApiHelper.makeUrlPath2({
            ms: AppConfig.CDP_MS.ENDPOINT,
            paths: ['miscs', 'track-staff-activity'],
            queryParams: { orgId: current.orgId, ...(AppHelper.isTeamsUnifiedView(current.orgId) ? AppHelper.getTeamApiParams(current.orgId) : {}) },
          }),
          jsonBody: {
            orgId: current.orgId,
            ...AppHelper.getTeamBodyParams(current.orgId, 'staffSubOrgId'),
            staffUserId: Controller.get().getUserId(),
            entityType: 'CRMCONTACT',
            entityId: current.id,
            entityName: Helper.formatName(current),
            entityEmail: current.email,
            entityPhone: current.phone,
            entityCreatedTs: current.createdTs,
            activityType: 'INTERACTION',
            subActivityType: subActivityType,
          }
        })
      }
    } catch(err) {
      console.log(err)
    }
  }

}
